import { installedItems } from "@p0security/shared/install/installed";
import type { McpIntegration } from "@p0security/shared/integrations/resources/mcp/components";
import { useAuthFetch } from "components/Login/hook";
import { useFirestoreDoc } from "providers/FirestoreProvider";
import { useCallback, useState } from "react";

export type McpClientCredentials = {
  readonly clientId: string;
  readonly clientSecret: string;
  readonly serverUrl: string;
};

export type McpClientRegistration = {
  credentials: McpClientCredentials | undefined;
  loading: boolean;
  error: Error | undefined;
  register: (hostname: string) => Promise<void>;
};

export const slugify = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

type McpClientCreateResp = {
  client: { id: string; secret: string };
};

// Backend allowlist today is ["claude-code"]. Widen once the platform union ships.
const REGISTRATION_PLATFORM = "claude-code";
const REGISTRATION_VERSION = "1.0.0";
const REGISTRATION_REDIRECT_URI = "https://mcp.p0.app/oauth/callback";
const FALLBACK_SERVER_URL = "https://mcp.p0.app";

export const useMcpClientRegistration = (): McpClientRegistration => {
  const authFetch = useAuthFetch();
  const { doc: mcpDoc } = useFirestoreDoc<McpIntegration>(`integrations/mcp`, {
    live: true,
    tenantAware: true,
  });
  const [credentials, setCredentials] = useState<
    McpClientCredentials | undefined
  >();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const register = useCallback(
    async (hostname: string) => {
      setLoading(true);
      setError(undefined);
      try {
        const createRes = await authFetch("mcp/clients", {
          method: "POST",
          json: {
            hostname,
            platform: REGISTRATION_PLATFORM,
            version: REGISTRATION_VERSION,
            redirectUri: REGISTRATION_REDIRECT_URI,
          },
        });
        if (!createRes) {
          throw new Error("Failed to register MCP client");
        }
        const { client } = (await createRes.json()) as McpClientCreateResp;
        // Prefer the first installed gateway URL from the integration doc;
        const config = mcpDoc?.data;
        const gatewayUrl = config
          ? Object.values(installedItems("gateway", config))[0]?.url
          : undefined;
        setCredentials({
          clientId: client.id,
          clientSecret: client.secret,
          serverUrl: gatewayUrl ?? FALLBACK_SERVER_URL,
        });
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [authFetch, mcpDoc]
  );

  return { credentials, loading, error, register };
};
